import { RiArrowRightLine } from '@remixicon/react'
import { CircleHelp } from 'lucide-react'
import { type ReactNode } from 'react'
import { Link } from 'react-router'

import {
	TemplateDownloadList,
	type Props as TemplateDownloadListProps,
} from '#app/components/mocra/template-download-list.tsx'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '#app/components/ui/dialog.tsx'
import { Typography } from '#app/components/ui/typography.tsx'
import {
	type SectionKey,
	SECTIONS,
} from '#app/routes/mocra+/_sections+/section-data.ts'
import { cn } from '#app/utils/misc.tsx'

interface Props {
	title: ReactNode
	description: ReactNode
	children: ReactNode
	meta?: ReactNode
	childrenClassName?: string
	templates?: TemplateDownloadListProps['templates']
	mocraSections?: readonly SectionKey[]
}

export function Section({
	title,
	description,
	children,
	meta,
	childrenClassName,
	templates,
	mocraSections,
}: Props) {
	const relatedSections = (mocraSections ?? []).map((sectionKey) => {
		const section = SECTIONS[sectionKey]
		return {
			id: section.id,
			title: section.title,
			to: `/mocra/${section.id}`,
		}
	})
	const templateList = templates?.length ? templates : null
	const hasRelatedSections = relatedSections.length > 0
	const hasDialogContent = hasRelatedSections || Boolean(templateList)
	const srTitle =
		typeof title === 'string'
			? title
			: typeof title === 'number'
				? String(title)
				: 'this section'

	return (
		<section className="flex flex-col gap-6 sm:flex-row">
			<div className="w-full max-w-xs space-y-3">
				<div className="space-y-2">
					<Typography.Subtitle className="flex w-full items-center gap-2">
						<span>{title}</span>
						{hasDialogContent ? (
							<Dialog>
								<DialogTrigger asChild>
									<button
										type="button"
										className="text-muted-foreground hover:text-foreground focus-visible:ring-primary ml-auto inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-full focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
									>
										<CircleHelp className="h-3.5 w-3.5" />
										<span className="sr-only">
											Open resources for {srTitle}
										</span>
									</button>
								</DialogTrigger>
								<DialogContent>
									<DialogHeader>
										<DialogTitle>Resources</DialogTitle>
										<DialogDescription>
											Quick links that connect this intake block to MoCRA
											requirements.
										</DialogDescription>
									</DialogHeader>
									<div className="space-y-6">
										{hasRelatedSections ? (
											<div className="space-y-2">
												<p className="text-muted-foreground text-[11px] font-semibold uppercase">
													MoCRA sections
												</p>
												<ul className="list-disc space-y-1.5 pl-5">
													{relatedSections.map((section) => (
														<li key={section.id}>
															<Link
																to={section.to}
																target="_blank"
																rel="noreferrer"
																className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-sm leading-5 underline-offset-2 transition-colors hover:underline"
															>
																<span>{section.title}</span>
																<RiArrowRightLine className="h-3.5 w-3.5" />
															</Link>
														</li>
													))}
												</ul>
											</div>
										) : null}
										{templateList ? (
											<div className="space-y-2">
												<p className="text-muted-foreground text-[11px] font-semibold uppercase">
													SOP templates
												</p>
												<TemplateDownloadList
													templates={templateList}
													linkTarget="_blank"
													linkRel="noreferrer"
												/>
											</div>
										) : null}
									</div>
								</DialogContent>
							</Dialog>
						) : null}
					</Typography.Subtitle>
					<Typography.Description>{description}</Typography.Description>
				</div>
				{meta ? <div>{meta}</div> : null}
			</div>
			<div className={cn('w-full space-y-3', childrenClassName)}>
				{children}
			</div>
		</section>
	)
}
